import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, X as XIcon } from "lucide-react";

const DISMISS_KEY = "dcc-update-dismissed";
const DISMISS_HOURS = 24;

/* ── Standalone detection ── */
function getIsStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    navigator.standalone === true
  );
}

/** Check whether the dismiss cooldown is still active. */
function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_HOURS * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * Slim "Update available" banner for the installed PWA.
 *
 * Detection strategy (belt-and-suspenders):
 *  1. On mount, reads window.__swRegistration (set by index.html) and checks .waiting.
 *  2. On mount, calls registration.update() to force an immediate byte-check
 *     with the server so the browser discovers a new SW as fast as possible.
 *  3. Listens for the "dcc:sw-update-waiting" custom event dispatched by index.html.
 *  4. Attaches its own "updatefound" listener to catch updates that arrive
 *     while the component is mounted.
 *  5. On visibilitychange (app returns from background), calls registration.update()
 *     again so returning users see the banner almost instantly after a republish.
 *
 * Refresh behaviour:
 *  - Posts { type: "SKIP_WAITING" } to the waiting SW.
 *  - Listens for "controllerchange" to reload once the new SW has claimed the page.
 *  - Falls back to a hard reload after 3 s if controllerchange doesn't fire.
 */
export function UpdateBanner() {
  const [updateReady, setUpdateReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const waitingWorkerRef = useRef(null);
  const refreshingRef = useRef(false);
  const isStandalone = getIsStandalone();

  /** Capture the waiting SW and surface the banner. */
  const flagUpdate = useCallback((sw) => {
    if (sw) {
      waitingWorkerRef.current = sw;
      setUpdateReady(true);
    }
  }, []);

  /** Resolve the registration (index.html's stored copy or the ready promise). */
  const getRegistration = useCallback(async () => {
    if (window.__swRegistration) return window.__swRegistration;
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.ready;
      window.__swRegistration = reg;
      return reg;
    }
    return null;
  }, []);

  /** Watch a newly-installing SW for it to reach "installed" state. */
  const watchInstalling = useCallback(
    (worker) => {
      if (!worker) return;
      const onChange = () => {
        if (
          worker.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          flagUpdate(worker);
        }
      };
      // Already installed? flag immediately.
      if (worker.state === "installed" && navigator.serviceWorker.controller) {
        flagUpdate(worker);
      } else {
        worker.addEventListener("statechange", onChange);
      }
    },
    [flagUpdate]
  );

  useEffect(() => {
    if (!isStandalone) return;
    if (isDismissed()) {
      setDismissed(true);
      return;
    }

    let cleanedUp = false;

    /* ─ 1. Custom event from index.html ─ */
    function onCustomWaiting() {
      const r = window.__swRegistration;
      if (r && r.waiting) flagUpdate(r.waiting);
    }
    window.addEventListener("dcc:sw-update-waiting", onCustomWaiting);

    /* ─ 2. Proactive check + listeners ─ */
    async function init() {
      const reg = await getRegistration();
      if (!reg || cleanedUp) return;

      // Already waiting? show immediately.
      if (reg.waiting) {
        flagUpdate(reg.waiting);
      }

      // Ask the browser to byte-check the SW script against the server RIGHT NOW.
      // This is the key call that makes the banner appear quickly after a republish.
      try {
        await reg.update();
      } catch {
        /* network error – ignore */
      }

      // After update(), check once more (the update may have downloaded instantly).
      if (!cleanedUp && reg.waiting) {
        flagUpdate(reg.waiting);
      }

      // If the installing worker is already downloading, watch it.
      if (reg.installing) {
        watchInstalling(reg.installing);
      }

      // Future updates that arrive while the page is open.
      reg.addEventListener("updatefound", () => {
        watchInstalling(reg.installing);
      });
    }
    init();

    /* ─ 3. Visibility change: re-check when the app comes back to foreground ─ */
    async function onVisible() {
      if (document.visibilityState !== "visible") return;
      const reg = await getRegistration();
      if (!reg || cleanedUp) return;

      // If a previous visit already installed a new SW, it may be waiting now.
      if (reg.waiting) {
        flagUpdate(reg.waiting);
        return;
      }

      // Force a fresh byte-check; the updatefound listener above will catch it.
      try {
        await reg.update();
      } catch {
        /* offline */
      }
      if (!cleanedUp && reg.waiting) {
        flagUpdate(reg.waiting);
      }
    }
    document.addEventListener("visibilitychange", onVisible);

    /* ─ 4. Controller change: the new SW took over → reload the page ─ */
    function onControllerChange() {
      // This only fires when a *new* SW has called clients.claim().
      // Whether the user tapped Refresh or the SW self-activated, reload cleanly.
      window.location.reload();
    }
    let swContainer = null;
    if ("serviceWorker" in navigator) {
      swContainer = navigator.serviceWorker;
      swContainer.addEventListener("controllerchange", onControllerChange);
    }

    return () => {
      cleanedUp = true;
      window.removeEventListener("dcc:sw-update-waiting", onCustomWaiting);
      document.removeEventListener("visibilitychange", onVisible);
      if (swContainer) {
        swContainer.removeEventListener("controllerchange", onControllerChange);
      }
    };
  }, [isStandalone, flagUpdate, getRegistration, watchInstalling]);

  /* ── Hide conditions ── */
  if (!isStandalone || !updateReady || dismissed) return null;

  function handleDismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* private browsing */
    }
  }

  function handleRefresh() {
    setRefreshing(true);
    refreshingRef.current = true;
    const waiting = waitingWorkerRef.current;
    if (waiting) {
      waiting.postMessage({ type: "SKIP_WAITING" });
    }
    // Safety net: if controllerchange never fires (e.g. the SW is already
    // active, or claim() fails), force-reload after 3 seconds so the user
    // is never stuck staring at a spinner.
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  }

  return (
    <div
      data-testid="update-app-banner"
      className="relative flex items-center gap-3 px-4 py-2.5 mb-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--accent)/0.08)]"
    >
      {/* Icon */}
      <div className="shrink-0 h-9 w-9 rounded-lg bg-[hsl(var(--accent)/0.15)] flex items-center justify-center">
        <RefreshCw className="h-4 w-4 text-[hsl(var(--accent))]" />
      </div>

      {/* Copy */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground leading-tight">
          Update available
        </div>
        <div className="text-xs text-muted-foreground leading-tight mt-0.5">
          A new version of the app is ready
        </div>
      </div>

      {/* Refresh button */}
      <Button
        onClick={handleRefresh}
        disabled={refreshing}
        size="sm"
        className="shrink-0 h-9 px-4 rounded-lg bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-[hsl(var(--accent-foreground))] font-semibold text-xs"
        data-testid="update-app-refresh-button"
      >
        <RefreshCw
          className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`}
        />
        {refreshing ? "Updating…" : "Refresh"}
      </Button>

      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        className="shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--secondary))] transition-colors"
        aria-label="Dismiss update banner"
        data-testid="update-app-dismiss"
      >
        <XIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
